import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

const BRAND = {
  black: '#000000',
  white: '#ffffff',
  blue: '#147efb',
  gold: '#fdbf00',
}

function formatSchedule(eventDate, startTime, endTime) {
  if (!eventDate || !startTime || !endTime) return ''
  try {
    const d = new Date(`${eventDate}T00:00:00`)
    const datePart = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    const fmtTime = (t) => {
      const [hh, mm] = String(t).split(':').map(Number)
      const dt = new Date()
      dt.setHours(hh || 0, mm || 0, 0, 0)
      return dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    }
    return `${datePart} · ${fmtTime(startTime)} – ${fmtTime(endTime)}`
  } catch {
    return ''
  }
}

function isCompleteDay(d) {
  return d && typeof d === 'object'
    && typeof d.date === 'string' && d.date
    && typeof d.start_time === 'string' && d.start_time
    && typeof d.end_time === 'string' && d.end_time
}

export default function OpenHouseLanding({ slug }) {
  const [ohStatus, setOhStatus] = useState('loading') // 'loading' | 'active' | 'none'
  const [openHouse, setOpenHouse] = useState(null)
  const [scheduleDays, setScheduleDays] = useState([]) // array of complete day objects
  const [photos, setPhotos] = useState([])
  const [slide, setSlide] = useState(0)

  useEffect(() => {
    let cancelled = false
    if (!slug) { setOhStatus('none'); return }
    supabase
      .rpc('get_open_house_public', { p_slug: slug })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          console.error('[get_open_house_public] failed:', error)
          setOhStatus('none')
          return
        }
        const row = Array.isArray(data) ? data[0] : data
        if (!row) { setOhStatus('none'); return }
        setOpenHouse(row)
        // Prefer the schedule JSONB array; fall back to legacy event_date/start_time/end_time
        let days = []
        if (Array.isArray(row.schedule) && row.schedule.length > 0) {
          days = row.schedule.filter(isCompleteDay)
        }
        if (days.length === 0 && row.event_date && row.start_time && row.end_time) {
          days = [{
            date: row.event_date,
            start_time: String(row.start_time).slice(0, 5),
            end_time: String(row.end_time).slice(0, 5),
          }]
        }
        setScheduleDays(days)
        const urls = Array.isArray(row.photos) ? row.photos.filter(u => typeof u === 'string' && u.trim()) : []
        setPhotos(urls)
        setOhStatus('active')
      })
    return () => { cancelled = true }
  }, [slug])

  const next = useCallback(() => {
    setSlide((s) => (photos.length ? (s + 1) % photos.length : 0))
  }, [photos.length])
  const prev = () => setSlide((s) => (photos.length ? (s - 1 + photos.length) % photos.length : 0))

  useEffect(() => {
    if (photos.length < 2) return
    const id = setInterval(next, 6000)
    return () => clearInterval(id)
  }, [next, photos.length])

  const [cma, setCma] = useState({ address: '', name: '', phone: '', email: '' })
  const [cmaStatus, setCmaStatus] = useState({ state: 'idle', msg: '' })

  const submitLead = async (payload) => {
    setCmaStatus({ state: 'loading', msg: '' })
    try {
      const { data: inserted, error } = await supabase
        .from('open_house_leads')
        .insert({
          name: payload.name,
          phone: payload.phone,
          email: payload.email,
          property_address: payload.address,
          open_house_id: openHouse?.open_house_id,
          source: openHouse?.source_tag,
        })
        .select('id')
        .single()
      if (error) throw error
      setCmaStatus({
        state: 'success',
        msg: "Got it. We'll be in touch within 24 hours with your home's market value.",
      })
      setCma({ address: '', name: '', phone: '', email: '' })

      // Fire-and-forget: push to Follow Up Boss. A FUB failure must NOT affect
      // the user-facing success message — admin can manually retry from the
      // Leads tab if anything goes sideways.
      if (inserted?.id) {
        supabase.functions
          .invoke('send-openhouse-lead-to-fub', { body: { lead_id: inserted.id } })
          .then(({ data, error: fnErr }) => {
            if (fnErr) console.error('[fub push] invoke failed:', fnErr)
            else if (data?.error) console.error('[fub push] function returned error:', data.error)
          })
          .catch((e) => console.error('[fub push] threw:', e))
      }
    } catch (err) {
      console.error('[open_house_leads] insert failed:', err)
      const phoneDisplay = openHouse?.agent_phone || ''
      setCmaStatus({
        state: 'error',
        msg: phoneDisplay
          ? `Something went wrong. Call us at ${phoneDisplay} or try again.`
          : 'Something went wrong. Please try again.',
      })
    }
  }

  const handleCmaSubmit = (e) => {
    e.preventDefault()
    if (!cma.address.trim() || !cma.name.trim() || !cma.phone.trim() || !cma.email.trim()) return
    submitLead(cma)
  }

  const address = openHouse?.address || ''
  const hasCarousel = photos.length > 0
  const currentSlide = hasCarousel ? Math.min(slide, photos.length - 1) : 0

  return (
    <>
      <style>{css}</style>

      <div className="hp-page">
        <header className="hp-nav">
          <div className="hp-nav-inner">
            <Logo brokerage={openHouse?.brokerage} />
            {openHouse?.agent_phone && (
              <a href={`tel:${String(openHouse.agent_phone).replace(/[^\d+]/g, '')}`} className="hp-nav-cta">
                {openHouse.agent_phone}
              </a>
            )}
          </div>
        </header>

        {/* HERO */}
        <section className="hp-hero">
          <div className={`hp-hero-grid ${hasCarousel ? '' : 'hp-hero-grid-solo'}`}>
            <div className="hp-hero-text">
              <div className="hp-eyebrow">
                <span className="hp-dot" /> Live This Weekend · Phoenix Metro
              </div>
              <h1 className="hp-h1">
                Do Not Miss <br />
                <span className="hp-h1-accent">This Open House.</span>
              </h1>
              <p className="hp-sub">
                Come tour the property. No appointment, no pressure — just stop by.
              </p>

              {ohStatus === 'active' && (
                <div className="hp-address-card">
                  <div className="hp-address-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 21s-7-6.5-7-12a7 7 0 1 1 14 0c0 5.5-7 12-7 12z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </div>
                  <div className="hp-address-content">
                    <div className="hp-address-label">Open House Address</div>
                    <div className="hp-address-value">{address}</div>
                    {scheduleDays.length > 0 && (
                      <div className="hp-address-times">
                        {scheduleDays.map((d, i) => {
                          const s = formatSchedule(d.date, d.start_time, d.end_time)
                          return s ? <div key={i} className="hp-address-time">{s}</div> : null
                        })}
                      </div>
                    )}
                  </div>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hp-address-directions"
                  >
                    Get Directions →
                  </a>
                </div>
              )}

              {ohStatus === 'none' && (
                <div className="hp-no-oh">
                  <div className="hp-no-oh-title">No open house this week — check back soon!</div>
                  <div className="hp-no-oh-sub">In the meantime, get a free valuation on your home below.</div>
                </div>
              )}

              {ohStatus === 'loading' && (
                <div className="hp-no-oh hp-no-oh-loading">Loading open house details…</div>
              )}
            </div>

            {hasCarousel && (
              <div className="hp-carousel">
                <div className="hp-carousel-frame">
                  {photos.map((src, i) => (
                    <div
                      key={i}
                      className={`hp-slide ${i === currentSlide ? 'is-active' : ''}`}
                      style={{ backgroundImage: `url(${src})` }}
                      aria-hidden={i !== currentSlide}
                    />
                  ))}
                  <div className="hp-carousel-overlay" />
                  {address && (
                    <div className="hp-carousel-caption">{address}</div>
                  )}

                  {photos.length > 1 && (
                    <>
                      <button className="hp-arrow hp-arrow-left" onClick={prev} aria-label="Previous photo">‹</button>
                      <button className="hp-arrow hp-arrow-right" onClick={next} aria-label="Next photo">›</button>

                      <div className="hp-dots">
                        {photos.map((_, i) => (
                          <button
                            key={i}
                            className={`hp-dot-btn ${i === currentSlide ? 'is-active' : ''}`}
                            onClick={() => setSlide(i)}
                            aria-label={`Go to photo ${i + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* CMA SECTION */}
        <section className="hp-section hp-section-light">
          <div className="hp-container">
            <div className="hp-cma-grid">
              <div className="hp-cma-left">
                <div className="hp-tag hp-tag-blue">FREE HOME VALUATION</div>
                <h2 className="hp-h2">
                  What Could Your <span className="hp-blue">Home Sell For?</span>
                </h2>
                <p className="hp-body">
                  Not a Zestimate. Not a guess. A real Comparative Market
                  Analysis using active listings, pending sales, and recent
                  closings in your specific neighborhood — the same data
                  lenders and appraisers use to set prices that actually hold.
                </p>

                <ul className="hp-checklist">
                  <li><Check /> Pulled from live MLS data — not algorithms</li>
                  <li><Check /> Hyper-local to your block, not your ZIP</li>
                  <li><Check /> Zero obligation, zero pressure</li>
                  <li><Check /> Delivered within 24 hours</li>
                </ul>
              </div>

              <div className="hp-cma-right">
                <form className="hp-card" onSubmit={handleCmaSubmit} noValidate>
                  <h3 className="hp-card-title">Get Your Free Home Valuation</h3>

                  <Field
                    label="Property Address"
                    value={cma.address}
                    onChange={(v) => setCma({ ...cma, address: v })}
                    required
                    placeholder="1234 E Camelback Rd, Phoenix, AZ"
                    autoComplete="street-address"
                  />
                  <Field
                    label="Full Name"
                    value={cma.name}
                    onChange={(v) => setCma({ ...cma, name: v })}
                    required
                    placeholder="Jane Smith"
                    autoComplete="name"
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    value={cma.phone}
                    onChange={(v) => setCma({ ...cma, phone: v })}
                    required
                    placeholder="(602) 555-0123"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={cma.email}
                    onChange={(v) => setCma({ ...cma, email: v })}
                    required
                    placeholder="jane@example.com"
                    autoComplete="email"
                    inputMode="email"
                  />

                  <button
                    type="submit"
                    className="hp-btn hp-btn-gold"
                    disabled={cmaStatus.state === 'loading'}
                  >
                    {cmaStatus.state === 'loading'
                      ? 'Sending...'
                      : 'See What My Home Is Worth →'}
                  </button>

                  {cmaStatus.msg && (
                    <div className={`hp-status hp-status-${cmaStatus.state}`} role="status">
                      {cmaStatus.msg}
                    </div>
                  )}

                  <p className="hp-fine">
                    By submitting, you agree to be contacted by {openHouse?.brokerage} about your property. We don't spam.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER WITH TEAM */}
        <footer className="hp-footer">
          <div className="hp-container">
            <div className="hp-footer-top">
              <Logo inverted brokerage={openHouse?.brokerage} />
              <div className="hp-footer-tag">Questions? Reach out anytime.</div>
            </div>

            <div className="hp-team">
              <div className="hp-team-card">
                <div className="hp-team-name">{openHouse?.agent_name}</div>
                <div className="hp-team-title">Licensed Real Estate Agent · Arizona</div>
                {openHouse?.agent_email && (
                  <a href={`mailto:${openHouse.agent_email}`} className="hp-team-contact">{openHouse.agent_email}</a>
                )}
              </div>
            </div>

            <div className="hp-footer-bottom">
              <div>{openHouse?.brokerage} · Phoenix, AZ</div>
              <div className="hp-footer-fine">Equal Housing Opportunity · Licensed in Arizona</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

function Logo({ inverted = false, brokerage }) {
  return (
    <div className={`hp-logo ${inverted ? 'is-inverted' : ''}`}>
      <div className="hp-logo-mark">
        <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path d="M4 16 L16 4 L28 16 L28 28 L20 28 L20 20 L12 20 L12 28 L4 28 Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
          <circle cx="16" cy="16" r="2" fill={BRAND.gold} />
        </svg>
      </div>
      <div className="hp-logo-text">
        <span className="hp-logo-name">{brokerage}</span>
        {brokerage === 'HomePros Advantage' && (
          <span className="hp-logo-tag">ADVANTAGE</span>
        )}
      </div>
    </div>
  )
}

function Field({ label, value, onChange, type = 'text', required, placeholder, autoComplete, inputMode }) {
  return (
    <label className="hp-field">
      <span className="hp-field-label">
        {label} {required && <em>*</em>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        className="hp-input"
      />
    </label>
  )
}

function Check() {
  return (
    <span className="hp-check" aria-hidden="true">
      <svg viewBox="0 0 16 16" fill="none">
        <path d="M3 8.5 L6.5 12 L13 4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

const css = `
  :root {
    --hp-black: ${BRAND.black};
    --hp-white: ${BRAND.white};
    --hp-blue: ${BRAND.blue};
    --hp-gold: ${BRAND.gold};
    --hp-ink: #0a0a0a;
    --hp-ink-soft: #4a4a4a;
    --hp-line: #e8e8e8;
    --hp-bg-soft: #f7f7f5;
  }

  .hp-page {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: var(--hp-ink);
    background: var(--hp-white);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
  }
  .hp-page * { box-sizing: border-box; }

  .hp-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

  .hp-nav {
    position: sticky; top: 0; z-index: 50;
    background: var(--hp-white);
    border-bottom: 1px solid var(--hp-line);
  }
  .hp-nav-inner {
    max-width: 1200px; margin: 0 auto;
    padding: 16px 24px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .hp-nav-cta {
    font-weight: 700; color: var(--hp-black);
    text-decoration: none; font-size: 15px;
    padding: 10px 18px;
    border: 2px solid var(--hp-black);
    border-radius: 999px;
    transition: all 0.2s;
    min-height: 44px;
    display: inline-flex; align-items: center;
  }
  .hp-nav-cta:hover { background: var(--hp-black); color: var(--hp-white); }

  .hp-logo { display: flex; align-items: center; gap: 10px; color: var(--hp-black); }
  .hp-logo.is-inverted { color: var(--hp-white); }
  .hp-logo-mark {
    width: 40px; height: 40px;
    display: flex; align-items: center; justify-content: center;
    background: var(--hp-blue); color: var(--hp-white);
    border-radius: 8px;
  }
  .hp-logo-mark svg { width: 24px; height: 24px; }
  .hp-logo.is-inverted .hp-logo-mark { background: var(--hp-gold); color: var(--hp-black); }
  .hp-logo-text { display: flex; flex-direction: column; line-height: 1; }
  .hp-logo-name { font-weight: 800; font-size: 18px; letter-spacing: -0.02em; }
  .hp-logo-tag { font-size: 10px; font-weight: 600; letter-spacing: 0.18em; color: var(--hp-blue); margin-top: 3px; }
  .hp-logo.is-inverted .hp-logo-tag { color: var(--hp-gold); }

  .hp-hero {
    padding: 56px 0 80px;
    background:
      radial-gradient(circle at 90% 10%, rgba(20,126,251,0.08), transparent 50%),
      radial-gradient(circle at 10% 90%, rgba(253,191,0,0.06), transparent 50%),
      var(--hp-white);
  }
  .hp-hero-grid {
    max-width: 1200px; margin: 0 auto; padding: 0 24px;
    display: grid;
    grid-template-columns: 1fr 1.1fr;
    gap: 56px; align-items: center;
  }
  .hp-hero-grid-solo { grid-template-columns: 1fr; max-width: 720px; }
  .hp-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 600;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--hp-ink-soft); margin-bottom: 24px;
  }
  .hp-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--hp-blue);
    box-shadow: 0 0 0 4px rgba(20,126,251,0.15);
    animation: hp-pulse 2s infinite;
  }
  @keyframes hp-pulse {
    0%, 100% { box-shadow: 0 0 0 4px rgba(20,126,251,0.15); }
    50% { box-shadow: 0 0 0 8px rgba(20,126,251,0.05); }
  }

  .hp-h1 {
    font-size: clamp(40px, 5.5vw, 68px);
    line-height: 1.02; font-weight: 800;
    letter-spacing: -0.035em;
    margin: 0 0 20px; color: var(--hp-black);
  }
  .hp-h1-accent { color: var(--hp-blue); font-style: italic; font-weight: 700; }
  .hp-sub {
    font-size: clamp(17px, 1.8vw, 19px);
    color: var(--hp-ink-soft);
    margin: 0 0 28px; max-width: 480px;
  }

  /* ADDRESS CARD */
  .hp-address-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px 22px;
    background: var(--hp-white);
    border: 2px solid var(--hp-black);
    border-radius: 14px;
    box-shadow: 6px 6px 0 var(--hp-gold);
    max-width: 540px;
    flex-wrap: wrap;
  }
  .hp-address-icon {
    width: 44px; height: 44px;
    border-radius: 10px;
    background: var(--hp-gold);
    color: var(--hp-black);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .hp-address-icon svg { width: 24px; height: 24px; }
  .hp-address-content {
    flex: 1;
    min-width: 200px;
  }
  .hp-address-label {
    font-size: 10px; font-weight: 800;
    letter-spacing: 0.16em; text-transform: uppercase;
    color: var(--hp-blue);
    margin-bottom: 4px;
  }
  .hp-address-value {
    font-size: 17px; font-weight: 700;
    color: var(--hp-black);
    line-height: 1.25;
    margin-bottom: 4px;
  }
  .hp-address-times {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 2px;
  }
  .hp-address-times .hp-address-time + .hp-address-time {
    padding-top: 4px;
    border-top: 1px dashed rgba(0,0,0,0.08);
  }
  .hp-address-time {
    font-size: 13px;
    color: var(--hp-ink-soft);
    font-weight: 500;
    line-height: 1.35;
  }
  .hp-address-directions {
    font-size: 14px;
    font-weight: 700;
    color: var(--hp-blue);
    text-decoration: none;
    padding: 12px 18px;
    border: 1.5px solid var(--hp-blue);
    border-radius: 999px;
    transition: all 0.2s;
    white-space: nowrap;
    min-height: 44px;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .hp-address-directions:hover {
    background: var(--hp-blue);
    color: var(--hp-white);
  }

  /* FALLBACK PANEL — no active open house */
  .hp-no-oh {
    padding: 20px 22px;
    background: var(--hp-bg-soft);
    border: 1.5px dashed #cfcfcf;
    border-radius: 14px;
    max-width: 540px;
  }
  .hp-no-oh-title {
    font-size: 17px; font-weight: 700;
    color: var(--hp-black);
    margin-bottom: 4px;
  }
  .hp-no-oh-sub {
    font-size: 14px;
    color: var(--hp-ink-soft);
  }
  .hp-no-oh-loading {
    color: var(--hp-ink-soft); font-style: italic;
  }

  /* CAROUSEL */
  .hp-carousel-frame {
    position: relative; aspect-ratio: 4 / 3;
    border-radius: 20px; overflow: hidden;
    background: var(--hp-ink);
    box-shadow: 0 30px 60px -20px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05);
  }
  .hp-slide {
    position: absolute; inset: 0;
    background-size: cover; background-position: center;
    opacity: 0; transition: opacity 0.8s ease;
  }
  .hp-slide.is-active { opacity: 1; }
  .hp-carousel-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.55), transparent 50%);
    pointer-events: none;
  }
  .hp-carousel-caption {
    position: absolute; left: 24px; bottom: 64px; right: 24px;
    color: var(--hp-white); font-weight: 600; font-size: 15px;
    padding: 8px 14px;
    background: rgba(0,0,0,0.4);
    backdrop-filter: blur(8px);
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.15);
    text-align: center;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .hp-arrow {
    position: absolute; top: 50%; transform: translateY(-50%);
    width: 44px; height: 44px;
    border-radius: 50%;
    background: rgba(255,255,255,0.95);
    border: none; cursor: pointer;
    font-size: 24px; line-height: 1;
    color: var(--hp-black);
    display: flex; align-items: center; justify-content: center;
    transition: transform 0.2s;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .hp-arrow:hover { transform: translateY(-50%) scale(1.08); }
  .hp-arrow-left { left: 16px; }
  .hp-arrow-right { right: 16px; }

  .hp-dots {
    position: absolute; bottom: 20px; left: 0; right: 0;
    display: flex; gap: 8px; justify-content: center;
  }
  .hp-dot-btn {
    width: 24px; height: 12px; border-radius: 6px;
    background: transparent;
    border: none; cursor: pointer; padding: 0;
    transition: background 0.2s, width 0.2s;
    position: relative;
  }
  .hp-dot-btn::after {
    content: ''; position: absolute; left: 0; right: 0; top: 4px;
    height: 4px; border-radius: 2px;
    background: rgba(255,255,255,0.4);
    transition: background 0.2s;
  }
  .hp-dot-btn.is-active { width: 32px; }
  .hp-dot-btn.is-active::after { background: var(--hp-gold); }

  .hp-section { padding: 96px 0; }
  .hp-section-light { background: var(--hp-bg-soft); }

  .hp-tag {
    display: inline-block;
    font-size: 11px; font-weight: 800; letter-spacing: 0.18em;
    padding: 6px 14px; border-radius: 999px;
    margin-bottom: 16px;
  }
  .hp-tag-blue { background: var(--hp-blue); color: var(--hp-white); }

  .hp-h2 {
    font-size: clamp(32px, 4vw, 48px);
    line-height: 1.05; font-weight: 800;
    letter-spacing: -0.03em; margin: 0 0 16px;
  }
  .hp-blue { color: var(--hp-blue); font-style: italic; }

  .hp-body { font-size: 17px; line-height: 1.65; color: var(--hp-ink-soft); margin: 0 0 28px; }

  .hp-checklist { list-style: none; padding: 0; margin: 0; }
  .hp-checklist li {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 0; font-weight: 500; font-size: 16px;
    color: var(--hp-black);
  }
  .hp-check {
    width: 24px; height: 24px; border-radius: 50%;
    background: var(--hp-blue); color: var(--hp-white);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .hp-check svg { width: 14px; height: 14px; }

  .hp-cma-grid {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 64px; align-items: center;
  }

  .hp-card {
    background: var(--hp-white);
    padding: 36px; border-radius: 16px;
    box-shadow: 0 30px 60px -20px rgba(0,0,0,0.18);
    border: 1px solid var(--hp-line);
    display: flex; flex-direction: column; gap: 16px;
  }
  .hp-card-title {
    font-size: 22px; font-weight: 800;
    margin: 0 0 8px; letter-spacing: -0.02em;
  }

  .hp-field { display: block; }
  .hp-field-label {
    display: block;
    font-size: 12px; font-weight: 700;
    letter-spacing: 0.08em; text-transform: uppercase;
    margin-bottom: 6px; color: inherit; opacity: 0.85;
  }
  .hp-field-label em { color: var(--hp-gold); font-style: normal; }

  .hp-input {
    width: 100%; padding: 14px 16px;
    font-size: 16px; font-family: inherit;
    border: 1.5px solid var(--hp-line);
    border-radius: 10px;
    background: var(--hp-white); color: var(--hp-ink);
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
    min-height: 48px;
  }
  .hp-input:focus {
    border-color: var(--hp-blue);
    box-shadow: 0 0 0 4px rgba(20,126,251,0.12);
  }

  .hp-btn {
    appearance: none; border: none;
    padding: 16px 24px; font-size: 16px; font-weight: 700;
    font-family: inherit; border-radius: 10px;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
    letter-spacing: -0.01em;
    min-height: 52px;
  }
  .hp-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .hp-btn-gold {
    background: var(--hp-blue); color: var(--hp-white);
    box-shadow: 0 8px 20px -8px rgba(20,126,251,0.6);
    margin-top: 8px;
  }
  .hp-btn-gold:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px -8px rgba(20,126,251,0.7);
  }

  .hp-status {
    margin-top: 12px; padding: 12px 16px;
    border-radius: 10px; font-size: 14px; font-weight: 500;
  }
  .hp-status-success {
    background: rgba(20,126,251,0.1); color: var(--hp-blue);
    border: 1px solid rgba(20,126,251,0.3);
  }
  .hp-status-error {
    background: rgba(239,68,68,0.1); color: #dc2626;
    border: 1px solid rgba(239,68,68,0.3);
  }

  .hp-fine {
    font-size: 12px; color: var(--hp-ink-soft);
    margin: 8px 0 0; line-height: 1.5;
  }

  /* FOOTER */
  .hp-footer {
    background: var(--hp-black); color: var(--hp-white);
    padding: 56px 0 32px;
  }
  .hp-footer-top {
    display: flex; justify-content: space-between; align-items: center;
    gap: 24px; flex-wrap: wrap;
    padding-bottom: 32px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    margin-bottom: 32px;
  }
  .hp-footer-tag {
    font-size: 14px;
    color: rgba(255,255,255,0.7);
    font-style: italic;
  }

  .hp-team {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 40px;
  }
  .hp-team-card {
    padding: 24px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .hp-team-name {
    font-size: 18px;
    font-weight: 800;
    color: var(--hp-white);
    letter-spacing: -0.02em;
  }
  .hp-team-title {
    font-size: 13px;
    color: var(--hp-gold);
    font-weight: 600;
    letter-spacing: 0.04em;
    margin-bottom: 12px;
  }
  .hp-team-contact {
    font-size: 14px;
    color: rgba(255,255,255,0.8);
    text-decoration: none;
    padding: 8px 0;
    transition: color 0.2s;
    word-break: break-word;
    min-height: 32px;
    display: inline-flex; align-items: center;
  }
  .hp-team-contact:hover { color: var(--hp-blue); }

  .hp-footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 13px;
    color: rgba(255,255,255,0.5);
    padding-top: 24px;
    border-top: 1px solid rgba(255,255,255,0.1);
  }
  .hp-footer-fine {
    font-size: 12px;
    color: rgba(255,255,255,0.4);
  }

  @media (max-width: 900px) {
    .hp-hero { padding: 32px 0 56px; }
    .hp-hero-grid { grid-template-columns: 1fr; gap: 40px; }
    .hp-cma-grid { grid-template-columns: 1fr; gap: 40px; }
    .hp-team { grid-template-columns: 1fr; gap: 16px; }
    .hp-section { padding: 64px 0; }
    .hp-card { padding: 24px; }
    .hp-carousel-caption { bottom: 56px; font-size: 13px; }
    .hp-address-card { box-shadow: 4px 4px 0 var(--hp-gold); }
    .hp-address-directions { width: 100%; text-align: center; }
  }

  @media (max-width: 480px) {
    .hp-arrow { width: 44px; height: 44px; font-size: 22px; }
    .hp-arrow-left { left: 10px; }
    .hp-arrow-right { right: 10px; }
    .hp-nav-cta { padding: 10px 14px; font-size: 14px; min-height: 44px; }
    .hp-nav-inner { padding: 12px 16px; }
    .hp-container { padding: 0 16px; }
    .hp-address-card { padding: 16px; gap: 12px; }
    .hp-address-value { font-size: 15px; }
    .hp-address-icon { width: 40px; height: 40px; }
    .hp-h1 { font-size: clamp(32px, 9vw, 44px); }
    .hp-h2 { font-size: clamp(26px, 7vw, 34px); }
    .hp-section { padding: 48px 0; }
    .hp-hero { padding: 24px 0 40px; }
    .hp-card { padding: 20px; }
  }
`
